import { useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  CalendarClock,
  CalendarPlus,
  GripVertical,
  Keyboard,
  Link2,
  ListChecks,
  Mail,
  Mic,
  Pencil,
  Pin,
  PinOff,
  Trash2,
  Wand2,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { inkOn, paperStyle, scrimStyle } from "@/lib/postItStyles";
import { shortDate } from "@/lib/eventMeta";
import { Markdown } from "@/components/Markdown";

const SOURCE_ICON = { voice: Mic, email: Mail, typed: Keyboard };

const Chip = ({ children, light, testId }) => (
  <span
    data-testid={testId}
    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.7rem] font-semibold uppercase tracking-wider"
    style={{
      background: light ? "rgba(255,255,255,0.18)" : "rgba(23,23,23,0.10)",
      border: `1px solid ${light ? "rgba(255,255,255,0.26)" : "rgba(23,23,23,0.16)"}`,
    }}
  >
    {children}
  </span>
);

export const PostItCard = ({
  note,
  canWrite,
  imageUrl,
  audioUrl,
  onEdit,
  onDelete,
  onPin,
  onMove,
  onSpawn,
  dragHandlers = {},
  dragging = false,
}) => {
  const paper = paperStyle(note.color, note.texture);
  const light = inkOn(note.color) === "light";
  const [open, setOpen] = useState(false);
  const [turnOpen, setTurnOpen] = useState(false);
  const long = (note.body || "").length > 420;
  const SourceIcon = SOURCE_ICON[note.source] || Keyboard;
  const hasImage = note.has_image && imageUrl;
  const scrim = hasImage ? scrimStyle(note.color) : null;

  return (
    <article
      data-testid={`postit-${note.post_it_id}`}
      data-postit-id={note.post_it_id}
      {...dragHandlers}
      className={`relative mb-5 break-inside-avoid rounded-sm p-5 pt-6 shadow-[6px_8px_0_rgba(15,15,15,0.14)] transition-transform duration-200 hover:-translate-y-0.5 ${
        dragging ? "opacity-40" : ""
      } ${note.expired ? "grayscale-[0.35]" : ""}`}
      style={{
        ...paper,
        ...(hasImage
          ? { backgroundImage: `url(${imageUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
          : {}),
      }}
    >
      {note.texture === "tape" ? (
        <span
          aria-hidden
          className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 w-24 rotate-[-2deg]"
          style={{
            background: light ? "rgba(255,255,255,0.30)" : "rgba(255,255,255,0.55)",
            border: "1px solid rgba(23,23,23,0.10)",
          }}
        />
      ) : null}

      <div style={scrim ? { ...scrim, padding: "0.85rem", borderRadius: "2px" } : undefined}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {note.pinned ? (
              <Chip light={light} testId={`postit-pinned-${note.post_it_id}`}>
                <Pin className="h-3 w-3" /> Pinned
              </Chip>
            ) : null}
            {note.priority === "high" ? (
              <Chip light={light} testId={`postit-high-${note.post_it_id}`}>
                High
              </Chip>
            ) : null}
            {note.priority === "low" ? <Chip light={light}>Low</Chip> : null}
            <Chip light={light}>
              <SourceIcon className="h-3 w-3" /> {note.source}
            </Chip>
          </div>
          {canWrite ? (
            <span
              className="hidden lg:inline-flex cursor-grab active:cursor-grabbing opacity-45"
              title="Drag to rearrange"
            >
              <GripVertical className="h-4 w-4" />
            </span>
          ) : null}
        </div>

        {note.title ? (
          <h3
            data-testid={`postit-title-${note.post_it_id}`}
            className="mt-3 font-heading text-lg font-bold leading-snug"
          >
            {note.title}
          </h3>
        ) : null}

        <Markdown
          text={note.body}
          testId={`postit-body-${note.post_it_id}`}
          className={`mt-2 text-base leading-relaxed ${long && !open ? "line-clamp-[9]" : ""}`}
        />
        {long ? (
          <button
            data-testid={`postit-expand-${note.post_it_id}`}
            onClick={() => setOpen((v) => !v)}
            className="mt-1.5 text-xs font-semibold underline"
          >
            {open ? "Show less" : "Read all"}
          </button>
        ) : null}

        {note.expires_on ? (
          <p
            data-testid={`postit-expiry-${note.post_it_id}`}
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider"
          >
            <CalendarClock className="h-3.5 w-3.5" />
            {note.expired ? `Expired ${shortDate(note.expires_on)}` : `Until ${shortDate(note.expires_on)}`}
          </p>
        ) : null}

        {note.matter_title || note.event_label || note.todo_title ? (
          <div className="mt-3 space-y-1 text-xs">
            {note.matter_title ? (
              <p className="flex items-center gap-1.5" data-testid={`postit-matter-${note.post_it_id}`}>
                <Link2 className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {note.matter_title}
                  {note.case_number ? ` · ${note.case_number}` : ""}
                </span>
              </p>
            ) : null}
            {note.event_label ? (
              <p className="flex items-center gap-1.5" data-testid={`postit-event-${note.post_it_id}`}>
                <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{note.event_label}</span>
              </p>
            ) : null}
            {note.todo_title ? (
              <p className="flex items-center gap-1.5" data-testid={`postit-todo-${note.post_it_id}`}>
                <ListChecks className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{note.todo_title}</span>
              </p>
            ) : null}
          </div>
        ) : null}

        {note.has_audio && audioUrl ? (
          <audio
            controls
            preload="none"
            src={audioUrl}
            data-testid={`postit-audio-${note.post_it_id}`}
            className="mt-3 h-9 w-full"
          />
        ) : null}

        {canWrite ? (
          <div className="mt-4 flex items-center gap-1 border-t pt-3" style={{ borderColor: light ? "rgba(255,255,255,0.22)" : "rgba(23,23,23,0.14)" }}>
            <button
              data-testid={`postit-edit-${note.post_it_id}`}
              onClick={() => onEdit?.(note)}
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-sm text-xs font-semibold hover:bg-black/10 transition-colors duration-200"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            <button
              data-testid={`postit-pin-${note.post_it_id}`}
              onClick={() => onPin?.(note)}
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-sm text-xs font-semibold hover:bg-black/10 transition-colors duration-200"
            >
              {note.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
              {note.pinned ? "Unpin" : "Pin"}
            </button>
            <Popover open={turnOpen} onOpenChange={setTurnOpen}>
              <PopoverTrigger asChild>
                <button
                  data-testid={`postit-turn-${note.post_it_id}`}
                  className="h-9 px-3 inline-flex items-center gap-1.5 rounded-sm text-xs font-semibold hover:bg-black/10 transition-colors duration-200"
                >
                  <Wand2 className="h-3.5 w-3.5" /> Turn into
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-2">
                <p className="label-caps px-2 py-1">Make this a…</p>
                {[
                  ["todo", 0, "To-do for today", ListChecks],
                  ["todo", 1, "To-do for tomorrow", ListChecks],
                  ["event", 0, "Diary entry today", CalendarPlus],
                  ["event", 1, "Diary entry tomorrow", CalendarPlus],
                ].map(([kind, offset, label, Icon]) => (
                  <button
                    key={`${kind}-${offset}`}
                    data-testid={`postit-to-${kind}-${offset ? "tomorrow" : "today"}-${note.post_it_id}`}
                    onClick={() => {
                      setTurnOpen(false);
                      onSpawn?.(note, kind, offset);
                    }}
                    className="w-full flex items-center gap-2 px-2 py-2.5 rounded-sm text-sm font-medium text-left hover:bg-accent transition-colors duration-200"
                  >
                    <Icon className="h-4 w-4 shrink-0" /> {label}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            <span className="ml-auto flex items-center gap-1">
              <button
                data-testid={`postit-up-${note.post_it_id}`}
                onClick={() => onMove?.(note, -1)}
                className="h-9 w-9 grid place-items-center rounded-sm hover:bg-black/10 transition-colors duration-200"
                aria-label="Move up"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                data-testid={`postit-down-${note.post_it_id}`}
                onClick={() => onMove?.(note, 1)}
                className="h-9 w-9 grid place-items-center rounded-sm hover:bg-black/10 transition-colors duration-200"
                aria-label="Move down"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
              <button
                data-testid={`postit-delete-${note.post_it_id}`}
                onClick={() => onDelete?.(note)}
                className="h-9 w-9 grid place-items-center rounded-sm hover:bg-black/10 transition-colors duration-200"
                aria-label="Delete note"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </span>
          </div>
        ) : null}
      </div>
    </article>
  );
};
